// src/pages/EditProfile.js
import React, { useEffect, useState } from "react";
import { auth, db } from "../firebase";
import { doc, getDoc, serverTimestamp, updateDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";

function emailToDocId(email) {
  return String(email || "").toLowerCase().replace(/[@.]/g, "_");
}

export default function EditProfile() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const [form, setForm] = useState({
    name: "",
    phone: "",
    address: "",
  });

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        setLoading(false);
        return;
      }

      try {
        const ref = doc(db, "users", emailToDocId(u.email));
        const snap = await getDoc(ref);
        const d = snap.exists() ? snap.data() : {};

        setForm({
          name: d.name || u.displayName || "",
          phone: d.phone || "",
          address: d.address || "",
        });

        setLoading(false);
      } catch (e) {
        console.error(e);
        setErr("Could not load your profile details.");
        setLoading(false);
      }
    });

    return () => unsub();
  }, []);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setErr("");
    setSaving(true);

    try {
      const u = auth.currentUser;
      if (!u?.email) throw new Error("Not signed in.");

      // Basic validation
      if (!form.name.trim()) throw new Error("Name is required.");

      const ref = doc(db, "users", emailToDocId(u.email));

      await updateDoc(ref, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        lastUpdatedByUser: serverTimestamp(),
      });

      navigate("/profile"); // or wherever your Profile route is
    } catch (e) {
      console.error(e);
      setErr(e.message || "Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 16 }}>Loading…</div>;

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: 16 }}>
      <h2 style={{ margin: "6px 0 14px" }}>Update Personal Information</h2>

      {err && (
        <div
          style={{
            background: "#fff1f2",
            border: "1px solid #fecaca",
            padding: 12,
            borderRadius: 12,
            marginBottom: 12,
          }}
        >
          {err}
        </div>
      )}

      <div style={{ display: "grid", gap: 12 }}>
        <label style={{ display: "grid", gap: 6 }}>
          <div style={{ fontWeight: 800 }}>Full Name</div>
          <input
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
            placeholder="e.g. Jennie Kaye"
            style={{ padding: 12, borderRadius: 12, border: "1px solid rgba(0,0,0,0.15)" }}
          />
        </label>

        <label style={{ display: "grid", gap: 6 }}>
          <div style={{ fontWeight: 800 }}>Phone</div>
          <input
            value={form.phone}
            onChange={(e) => setField("phone", e.target.value)}
            placeholder="e.g. +44 7788 191693"
            style={{ padding: 12, borderRadius: 12, border: "1px solid rgba(0,0,0,0.15)" }}
          />
        </label>

        <label style={{ display: "grid", gap: 6 }}>
          <div style={{ fontWeight: 800 }}>Address</div>
          <textarea
            value={form.address}
            onChange={(e) => setField("address", e.target.value)}
            placeholder="e.g. Flat 97, 30 Addiscombe Grove, CR0 5BY"
            rows={3}
            style={{ padding: 12, borderRadius: 12, border: "1px solid rgba(0,0,0,0.15)" }}
          />
        </label>

        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              padding: "12px 14px",
              borderRadius: 12,
              border: "1px solid rgba(0,0,0,0.15)",
              background: "#fff",
              fontWeight: 900,
              cursor: "pointer",
              flex: 1,
            }}
            disabled={saving}
          >
            Cancel
          </button>

          <button
            onClick={handleSave}
            style={{
              padding: "12px 14px",
              borderRadius: 12,
              border: "none",
              background: "#111827",
              color: "#fff",
              fontWeight: 900,
              cursor: "pointer",
              flex: 1,
              opacity: saving ? 0.7 : 1,
            }}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
